import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';


import { orderTrackingService } from '../../services/orderTrackingService';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Icon from '../../components/AppIcon';
import TrackingTimeline from './components/TrackingTimeline';
import ShipmentCard from './components/ShipmentCard';
import TrackingSearch from './components/TrackingSearch';

const OrderTrackingDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadShipments();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        orderTrackingService?.unsubscribe(realtimeChannel);
      }
    };
  }, [user, navigate]);

  useEffect(() => {
    applyFilters();
  }, [shipments, filterStatus, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      setError('');

      const isAdmin = userProfile?.role === 'admin';
      const data = isAdmin 
        ? await orderTrackingService?.getAllShipments()
        : await orderTrackingService?.getUserShipments();

      setShipments(data || []);
    } catch (err) {
      console.error('Error loading shipments:', err);
      setError(err?.message || 'Gönderi bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = orderTrackingService?.subscribeToShipmentUpdates((payload) => {
      if (payload?.eventType === 'INSERT') {
        setShipments(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setShipments(prev => prev?.map(s => 
          s?.id === payload?.new?.id ? { ...s, ...payload?.new } : s
        ));
      }
    });
    setRealtimeChannel(channel);
  };

  const applyFilters = () => {
    let filtered = [...shipments];

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(s => s?.currentStatus === filterStatus);
    }

    if (searchQuery) {
      filtered = filtered?.filter(s => 
        s?.trackingNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        s?.orderId?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredShipments(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      scheduled: { label: 'Planlandı', className: 'bg-blue-100 text-blue-800', icon: 'Calendar' },
      in_progress: { label: 'Yolda', className: 'bg-purple-100 text-purple-800', icon: 'Truck' },
      delivered: { label: 'Teslim Edildi', className: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      failed: { label: 'Başarısız', className: 'bg-red-100 text-red-800', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        <Icon name={config?.icon} size={14} />
        {config?.label}
      </span>
    );
  };

  const getCarrierName = (carrierCode) => {
    const carriers = {
      aras_kargo: 'Aras Kargo',
      mng_kargo: 'MNG Kargo',
      ptt_kargo: 'PTT Kargo',
      yurtici_kargo: 'Yurtiçi Kargo',
      ups_kargo: 'UPS Kargo'
    };
    return carriers?.[carrierCode] || carrierCode;
  };

  const stats = {
    total: shipments?.length || 0,
    inTransit: shipments?.filter(s => s?.currentStatus === 'in_progress')?.length || 0,
    delivered: shipments?.filter(s => s?.currentStatus === 'delivered')?.length || 0,
    pending: shipments?.filter(s => s?.currentStatus === 'pending')?.length || 0
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="bg-card rounded-xl shadow-organic p-8">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Sipariş Takip Paneli
            </h1>
            <p className="text-muted-foreground">
              Tüm siparişlerinizi gerçek zamanlı olarak takip edin
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Toplam Gönderi</span>
                <Icon name="Package" size={20} className="text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.total}</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Yolda</span>
                <Icon name="Truck" size={20} className="text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.inTransit}</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Teslim Edildi</span>
                <Icon name="CheckCircle" size={20} className="text-green-500" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.delivered}</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Beklemede</span>
                <Icon name="Clock" size={20} className="text-yellow-500" />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.pending}</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-card rounded-xl p-6 border border-border mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <TrackingSearch 
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Takip numarası veya sipariş ID ile ara..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === 'all' ?'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tümü
                </button>
                <button
                  onClick={() => setFilterStatus('in_progress')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === 'in_progress' ?'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Yolda
                </button>
                <button
                  onClick={() => setFilterStatus('delivered')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === 'delivered' ?'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Teslim Edildi
                </button>
              </div>
            </div>
          </div>

          {/* Shipments List */}
          <div className="space-y-4">
            {filteredShipments?.length === 0 ? (
              <div className="bg-card rounded-xl p-12 text-center border border-border">
                <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">Gönderi Bulunamadı</h3>
                <p className="text-muted-foreground">
                  {searchQuery || filterStatus !== 'all' ?'Arama kriterlerinize uygun gönderi bulunamadı' :'Henüz takip edilebilecek gönderi bulunmuyor'}
                </p>
              </div>
            ) : (
              filteredShipments?.map((shipment) => (
                <ShipmentCard
                  key={shipment?.id}
                  shipment={shipment}
                  onSelect={setSelectedShipment}
                  getStatusBadge={getStatusBadge}
                  getCarrierName={getCarrierName}
                />
              ))
            )}
          </div>

          {/* Tracking Detail Modal */}
          {selectedShipment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-card rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-1">
                      Takip Detayları
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {selectedShipment?.trackingNumber}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedShipment(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>
                <div className="p-6">
                  <TrackingTimeline 
                    shipment={selectedShipment}
                    getCarrierName={getCarrierName}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderTrackingDashboard;